import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, AlertTriangle, User, Award, Calendar, Hash, ArrowLeft } from 'lucide-react';
import AILoader from '../components/AILoader';
import { getBackendUrl } from '../utils/auth';

const CertificateVerify = () => {
  const { certNo } = useParams();
  const [certificateData, setCertificateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const backendUrl = getBackendUrl();
        const response = await fetch(`${backendUrl}/api/certificates/verify/${certNo}`);
        if (!response.ok) throw new Error('Certificate not found');
        const data = await response.json();
        setCertificateData(data.certificate);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [certNo]);

  if (loading) return <AILoader />;

  return (
    <div className="w-full min-h-screen bg-[#020617] text-white flex flex-col justify-between pt-24 pb-8 px-4 font-inter">
      {/* Background decoration elements */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#6D4CFF]/10 rounded-full blur-[120px] pointer-events-none z-0"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#2D8CFF]/10 rounded-full blur-[120px] pointer-events-none z-0"></div>

      <div className="max-w-2xl w-full mx-auto z-10 flex-grow flex flex-col justify-center my-8">

        {/* Logo / Header */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src="/cc.png" alt="CCAI Logo" className="w-10 h-10 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
          <div className="text-center">
            <span className="text-[#6D4CFF] text-[20px] font-bold tracking-[0.1em] block leading-none mb-1">CLIENTCODEAI</span>
            <span className="text-slate-400 text-[9px] tracking-[0.25em] block font-medium uppercase opacity-80">INNOVATION PVT. LTD.</span>
          </div>
        </div>

        {error ? (
          /* Error State Card */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-[#070e27]/80 border border-red-500/30 rounded-2xl p-8 text-center shadow-[0_0_50px_rgba(239,68,68,0.05)] backdrop-blur-md"
          >
            <div className="inline-flex p-4 rounded-full bg-red-500/10 mb-4 text-red-500">
              <AlertTriangle className="w-12 h-12" />
            </div>
            <h1 className="text-2xl font-bold text-red-500 mb-2">Verification Failed</h1>
            <p className="text-slate-300 max-w-md mx-auto mb-6 text-sm">
              The certificate ID <span className="font-mono text-red-400 font-bold">{certNo}</span> could not be verified on our servers. It may have been revoked or entered incorrectly.
            </p>
            <Link
              id="back-home-error-btn"
              to="/"
              className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors border border-slate-700"
            >
              <ArrowLeft className="w-4 h-4" /> Go back to Home
            </Link>
          </motion.div>
        ) : certificateData ? (
          /* Success Verification Card */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-[#070e27]/80 border border-green-500/30 rounded-2xl p-6 sm:p-8 shadow-[0_0_50px_rgba(34,197,94,0.08)] backdrop-blur-md relative overflow-hidden"
          >
            {/* Top decorative stripe */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-green-500 to-emerald-400"></div>

            {/* Verification Status Header */}
            <div className="flex flex-col sm:flex-row items-center gap-4 border-b border-slate-800 pb-6 mb-6">
              <div className="inline-flex p-3 rounded-full bg-green-500/15 text-green-400">
                <CheckCircle className="w-10 h-10" />
              </div>
              <div className="text-center sm:text-left">
                <h1 className="text-xl sm:text-2xl font-bold text-green-400">Verified Certificate</h1>
                <p className="text-slate-400 text-xs sm:text-sm mt-0.5">This certificate of completion is authentic and verified by ClientCodeAI.</p>
              </div>
            </div>

            {/* Details Fields */}
            <div className="space-y-4">
              {/* Student Name */}
              <div className="flex gap-4 p-3 rounded-xl bg-slate-900/40 border border-slate-800/40">
                <div className="p-2.5 rounded-lg bg-[#6D4CFF]/15 text-[#6D4CFF] self-center">
                  <User className="w-5 h-5" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-0.5">Recipient Name</span>
                  <span className="text-base sm:text-lg font-bold text-white leading-tight">{certificateData.studentName}</span>
                </div>
              </div>

              {/* Course Name */}
              <div className="flex gap-4 p-3 rounded-xl bg-slate-900/40 border border-slate-800/40">
                <div className="p-2.5 rounded-lg bg-[#2D8CFF]/15 text-[#2D8CFF] self-center">
                  <Award className="w-5 h-5" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-0.5">Program / Domain</span>
                  <span className="text-base sm:text-lg font-bold text-[#2D8CFF] leading-tight">{certificateData.courseName}</span>
                </div>
              </div>

              {/* Duration / Date */}
              <div className="flex gap-4 p-3 rounded-xl bg-slate-900/40 border border-slate-800/40">
                <div className="p-2.5 rounded-lg bg-[#7C4DFF]/15 text-[#7C4DFF] self-center">
                  <Calendar className="w-5 h-5" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-0.5">Internship Duration</span>
                  <span className="text-sm sm:text-base font-semibold text-slate-200">
                    {new Date(certificateData.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} to{' '}
                    {new Date(certificateData.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                </div>
              </div>

              {/* Certificate ID */}
              <div className="flex gap-4 p-3 rounded-xl bg-slate-900/40 border border-slate-800/40">
                <div className="p-2.5 rounded-lg bg-[#00D2FF]/15 text-[#00D2FF] self-center">
                  <Hash className="w-5 h-5" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-0.5">Certificate ID</span>
                  <span className="text-sm sm:text-base font-bold text-sky-400 font-mono">{certificateData.certificateNumber || certNo}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex justify-center">
              <Link
                id="back-home-success-btn"
                to="/"
                className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors border border-slate-700 text-slate-300 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4" /> Back to Home
              </Link>
            </div>

          </motion.div>
        ) : null}

      </div>

      {/* Footer copyright */}
      <div className="text-center text-xs text-slate-500 mt-auto">
        &copy; {new Date().getFullYear()} ClientCodeAI Innovation Pvt. Ltd. All rights reserved.
      </div>
    </div>
  );
};

export default CertificateVerify;
